import React from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import OnboardingRoutes from "./onboarding";
import PatientRoutes from "./patient";
import DoctorRoutes from "./doctor";
import ClinicRoutes from "./clinic";
import CommonRoutes from "./common";

import MobileTopNavigation from "@/components/Navigation/Mobile/MobileTopNavigation";
import MobileBottomNavigation from "@/components/Navigation/Mobile/MobileBottomNavigation";
import DoctorPharmacyDiagnosticTopNav from "@/components/Navigation/Desktop/DesktopTopNavigation/DoctorPharmacyDiagnosticTopNav";
import DesktopBottomNavigation from "@/components/Navigation/Desktop/DesktopBottomNavigation";
import PatientTopNav from "@/components/Navigation/Desktop/DesktopTopNavigation/PatientTopNav";
import CopyWriteFooter from "@/components/Navigation/Desktop/DesktopBottomNavigation/CopyWriteFooter";

import { storage } from "@/utils/Storage";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { isClinic } from "@/utils";

import { IRoleType } from "@/models/role";
import { ISessionKeys } from "@/models";

import { routes } from "./routes";

const Router: React.FC = () => {
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const isMobileOrTablet = isMobile || isTablet;

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const currentPath = location.pathname;
  const {
    doctorBottomNavRoutes,
    doctorRoutes,
    hideTopNavRoutes,
    onboardingRoutes,
    patientRoutes,
    pharmacyTopNavRoutes,
    routesWithoutNavsOnMobile,
    routesWithoutNavsOnTablet,
    hideDoctorOrJrDoctorTopFooterRoutes,
    termConditionPrivacyPolicy,
  } = routes;

  const isOnboarding = onboardingRoutes.includes(currentPath);
  const isPatientRoute = patientRoutes.includes(currentPath);
  const isTermConditionPrivacyRoute =
    termConditionPrivacyPolicy.includes(currentPath);
  const isRoutesWithoutNavsOnMobile =
    routesWithoutNavsOnMobile.includes(currentPath);
  const isRoutesWithoutNavsOnTablet =
    routesWithoutNavsOnTablet.includes(currentPath);
  const isPharmacyTopNavRoute = pharmacyTopNavRoutes.includes(currentPath);
  const isDoctorRoute = doctorRoutes.includes(currentPath);
  const isHideTopNav = hideTopNavRoutes.includes(currentPath);
  const isHideDoctorOrJrDoctorTopFooter =
    hideDoctorOrJrDoctorTopFooterRoutes.includes(currentPath);
  const showBottomNavForDoctor = doctorBottomNavRoutes.includes(currentPath);

  const allRoleExceptPatient =
    roleType === IRoleType.DOCTOR ||
    roleType === IRoleType.JR_DOCTOR ||
    roleType === IRoleType.PHARMACY ||
    roleType === IRoleType.DIAGNOSTIC_CENTER;

  const isClinicRole = isClinic();

  return (
    <>
      {!isOnboarding && !isHideTopNav && (
        <>
          {isMobile &&
            !isTermConditionPrivacyRoute &&
            !isRoutesWithoutNavsOnMobile &&
            isPatientRoute && (
              <>
                <MobileTopNavigation />
                <MobileBottomNavigation />
              </>
            )}
          {isTablet &&
            !isTermConditionPrivacyRoute &&
            !isRoutesWithoutNavsOnTablet &&
            isPatientRoute && (
              <>
                <MobileTopNavigation />
                <MobileBottomNavigation />
              </>
            )}
          {!isMobileOrTablet &&
            (isPatientRoute || isTermConditionPrivacyRoute) && (
              <PatientTopNav />
            )}

          {isDoctorRoute && isMobileOrTablet && <MobileTopNavigation />}
        </>
      )}
      {isMobileOrTablet && isPharmacyTopNavRoute && <MobileTopNavigation />}

      {!isMobileOrTablet &&
        allRoleExceptPatient &&
        !isHideDoctorOrJrDoctorTopFooter && <DoctorPharmacyDiagnosticTopNav />}

      {(showBottomNavForDoctor || isClinicRole) && isMobileOrTablet && (
        <MobileBottomNavigation />
      )}

      <Routes>
        {[
          ...OnboardingRoutes,
          ...PatientRoutes,
          ...DoctorRoutes,
          ...ClinicRoutes,
          ...CommonRoutes,
        ].map((route, index) => (
          <Route path={route.path} element={route.component} key={index} />
        ))}
      </Routes>

      {!isMobileOrTablet && (isPatientRoute || isTermConditionPrivacyRoute) && (
        <DesktopBottomNavigation />
      )}

      {!isMobileOrTablet &&
        !isHideDoctorOrJrDoctorTopFooter &&
        allRoleExceptPatient && <CopyWriteFooter />}
    </>
  );
};

export default Router;
